import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

const compactLabelStyle = {
  color: '#9fdcff',
  fontSize: '0.72rem',
  fontWeight: 750,
  marginBottom: 4,
};

const compactInputStyle = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '0.48rem 0.55rem',
  borderRadius: 7,
  border: '1px solid #2d8fc4',
  background: '#181a2a',
  color: '#ffffff',
  fontSize: '0.84rem',
};

const compactHintStyle = {
  color: '#789bbd',
  fontSize: '0.68rem',
  marginTop: 3,
};

function hasValue(value) {
  if (value === undefined || value === null) {
    return false;
  }

  if (typeof value === 'string') {
    return value.trim() !== '';
  }

  return true;
}

function firstValue(...values) {
  for (const value of values) {
    if (hasValue(value)) {
      return value;
    }
  }

  return '';
}

function toInputString(value) {
  return value === undefined || value === null
    ? ''
    : String(value);
}

function inferRouteTypeFromAirfields(departureAirfield, arrivalAirfield) {
  const departure = String(departureAirfield || '').trim().toUpperCase();
  const arrival = String(arrivalAirfield || '').trim().toUpperCase();

  if (!departure || !arrival) {
    return '';
  }

  return departure === arrival ? 'local' : 'land-away';
}

export default function AircraftTechLog({ prefill = {}, onSave, options = {} }) {
  const normalizeAirfieldCode = useCallback((value) => String(value || '').toUpperCase(), []);

  const normalizeLeg = useCallback((leg = {}) => ({
    from: normalizeAirfieldCode(leg.from ?? leg.departureAirfield ?? ''),
    to: normalizeAirfieldCode(leg.to ?? leg.arrivalAirfield ?? ''),
    takeoffTime: String(leg.takeoffTime ?? leg.departureTime ?? '').trim(),
    landingTime: String(leg.landingTime ?? leg.arrivalTime ?? '').trim(),
    hobbsStart: String(leg.hobbsStart ?? leg.methodStart ?? '').trim(),
    hobbsEnd: String(leg.hobbsEnd ?? leg.methodEnd ?? '').trim(),
  }), [normalizeAirfieldCode]);

  const isLegPopulated = useCallback((leg = {}) => {
    const normalized = normalizeLeg(leg);
    return !!(normalized.from || normalized.to || normalized.takeoffTime || normalized.landingTime || normalized.hobbsStart || normalized.hobbsEnd);
  }, [normalizeLeg]);

  const calculateLegMinutes = useCallback((takeoffTime, landingTime) => {
    if (!takeoffTime || !landingTime) return null;

    const [takeoffHour, takeoffMinute] = String(takeoffTime).split(':').map(Number);
    const [landingHour, landingMinute] = String(landingTime).split(':').map(Number);

    if (
      !Number.isFinite(takeoffHour) ||
      !Number.isFinite(takeoffMinute) ||
      !Number.isFinite(landingHour) ||
      !Number.isFinite(landingMinute)
    ) {
      return null;
    }

    let startMinutes = takeoffHour * 60 + takeoffMinute;
    let endMinutes = landingHour * 60 + landingMinute;

    if (endMinutes < startMinutes) {
      endMinutes += 24 * 60;
    }

    return endMinutes - startMinutes;
  }, []);

  const formatMinutesAsHoursMinutes = useCallback((minutes) => {
    if (!Number.isFinite(minutes) || minutes < 0) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${String(mins).padStart(2, '0')}m`;
  }, []);

  const formatDecimalHours = useCallback((value) => {
    const rounded = Math.round(value * 100) / 100;
    return String(rounded).replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1');
  }, []);

  const buildInitialLegs = useCallback(() => {
    const savedLegs = Array.isArray(prefill.legs) ? prefill.legs.map(normalizeLeg).filter(isLegPopulated) : [];
    if (savedLegs.length > 0) return savedLegs;

    const fallbackSingleLeg = normalizeLeg({
      from: prefill.departureAirfield,
      to: prefill.arrivalAirfield,
      takeoffTime: prefill.departureTime,
      landingTime: prefill.arrivalTime,
      hobbsStart: prefill.hobbsStart,
      hobbsEnd: prefill.hobbsEnd,
    });

    if (isLegPopulated(fallbackSingleLeg)) {
      return [fallbackSingleLeg];
    }

    const baseAirfield = normalizeAirfieldCode(prefill.bookingBaseAirfield);
    const destinationAirfield = normalizeAirfieldCode(prefill.bookingDestinationAirfield);
    const returnAirfield = normalizeAirfieldCode(prefill.bookingReturnAirfield);

    if (destinationAirfield && returnAirfield && destinationAirfield !== returnAirfield) {
      return [
        normalizeLeg({ from: baseAirfield, to: destinationAirfield }),
        normalizeLeg({ from: destinationAirfield, to: returnAirfield }),
      ];
    }

    const singleDestination = destinationAirfield || returnAirfield;
    if (singleDestination || baseAirfield) {
      return [normalizeLeg({ from: baseAirfield, to: singleDestination })];
    }

    return [normalizeLeg()];
  }, [prefill, normalizeAirfieldCode, normalizeLeg, isLegPopulated]);

  const resolveTaxiTime = useCallback(() => {
    if (prefill.taxiTime !== undefined && prefill.taxiTime !== null && String(prefill.taxiTime).trim() !== '') {
      return String(prefill.taxiTime);
    }

    if (options.defaultTaxiTimeEnabled === false) return '';

    const defaultTaxiMinutes = Number(options.defaultTaxiTimeMinutes);
    if (Number.isFinite(defaultTaxiMinutes) && defaultTaxiMinutes >= 0) {
      return formatDecimalHours(defaultTaxiMinutes / 60);
    }

    const legacyDefaultTaxiHours = Number(options.defaultTaxiTimeHours);
    if (Number.isFinite(legacyDefaultTaxiHours) && legacyDefaultTaxiHours >= 0) {
      return formatDecimalHours(legacyDefaultTaxiHours);
    }

    return '';
  }, [prefill.taxiTime, options.defaultTaxiTimeEnabled, options.defaultTaxiTimeMinutes, options.defaultTaxiTimeHours, formatDecimalHours]);

  const resolveDefect = useCallback(() => {
    const savedDefect = prefill.defect ?? prefill.snag;
    if (savedDefect !== undefined && savedDefect !== null && String(savedDefect).trim() !== '') {
      return String(savedDefect);
    }

    return 'Nil';
  }, [prefill.defect, prefill.snag]);

  const resolveDefectSeverity = useCallback(() => {
    const savedSeverity = String(prefill.defectSeverity || prefill.defect_severity || '').trim().toLowerCase();
    if (['minor', 'significant', 'safety_critical'].includes(savedSeverity)) {
      return savedSeverity;
    }
    return '';
  }, [prefill.defectSeverity, prefill.defect_severity]);

  const initialDefect = resolveDefect();
  const initialAircraftStatus = String(prefill.aircraftStatus || '').trim()
    || (initialDefect && initialDefect.toLowerCase() !== 'nil' ? 'defect_reported' : 'serviceable');

  const initialRouteType = String(
    firstValue(
      prefill.bookingFlightType,
      prefill.flightType,
      inferRouteTypeFromAirfields(
        prefill.departureAirfield,
        prefill.arrivalAirfield
      )
    )
  )
    .trim()
    .toLowerCase();
  const [recordingMethod, setRecordingMethod] = useState(firstValue(prefill.recordingMethod, options.recordingMethod, 'hobbs'));
  const [flightNumber, setFlightNumber] = useState(prefill.flightNumber || '');
  const [departureAirfield, setDepartureAirfield] = useState(normalizeAirfieldCode(prefill.departureAirfield));
  const [arrivalAirfield, setArrivalAirfield] = useState(normalizeAirfieldCode(prefill.arrivalAirfield));
  const [departureTime, setDepartureTime] = useState('');
  const [arrivalTime, setArrivalTime] = useState('');
  const [routeType, setRouteType] = useState(
    ['local', 'circuits', 'land-away'].includes(
      initialRouteType
    )
      ? initialRouteType
      : ''
  );

  const routeChoiceNeeded =
    prefill.bookingRouteRecorded === false;
  const [legs, setLegs] = useState(buildInitialLegs());
  const [methodStart, setMethodStart] = useState(firstValue(prefill.hobbsStart, prefill.methodStart, prefill.startReading));
  const [methodEnd, setMethodEnd] = useState(firstValue(prefill.hobbsEnd, prefill.methodEnd, prefill.endReading));
  const [flightTime, setFlightTime] = useState(prefill.flightTime || '');
  const [taxiTime, setTaxiTime] = useState(resolveTaxiTime());
  const [aircraftStatus, setAircraftStatus] = useState(initialAircraftStatus);
  const [defect, setDefect] = useState(resolveDefect());
  const [defectSeverity, setDefectSeverity] = useState(resolveDefectSeverity());
  const [signature, setSignature] = useState(toInputString(firstValue(prefill.signature, prefill.pilotName)));
  const [rectification, setRectification] = useState(prefill.rectification || '');
  const [engineerSignature, setEngineerSignature] = useState(prefill.engineerSignature || '');
  const [nextCheck, setNextCheck] = useState(prefill.nextCheck || '');
  const [fuelUpliftLitres, setFuelUpliftLitres] = useState(toInputString(firstValue(prefill.fuelUpliftLitres, prefill.fuel_uplift_litres)));
  const [oilUpliftLitres, setOilUpliftLitres] = useState(toInputString(firstValue(prefill.oilUpliftLitres, prefill.oil_uplift_litres)));
  const [remarks, setRemarks] = useState(toInputString(firstValue(prefill.remarks, prefill.notes)));
  const [validationError, setValidationError] = useState('');
  const [legFieldErrors, setLegFieldErrors] = useState({});

  const mergedOptions = {
    hobbsEnabled: true,
    flightTimeEnabled: true,
    taxiTimeEnabled: true,
    snagEnabled: true,
    notesEnabled: true,
    showOptionalFields: true,
    ...options,
  };

  const methodLabel = String(recordingMethod || 'hobbs').trim() || 'Hobbs';
  const methodLabelTitle = methodLabel.charAt(0).toUpperCase() + methodLabel.slice(1).toLowerCase();

  const legsWithCarryForward = useMemo(() => {
    const nextLegs = (legs || []).map(normalizeLeg);

    for (let index = 1; index < nextLegs.length; index += 1) {
      const previousEnd = nextLegs[index - 1]?.hobbsEnd || '';
      if (previousEnd) {
        nextLegs[index] = {
          ...nextLegs[index],
          hobbsStart: previousEnd,
        };
      }
    }

    return nextLegs;
  }, [legs, normalizeLeg]);

  const legSummaries = useMemo(() => (
    (legsWithCarryForward || []).map((leg) => {
      const minutes = calculateLegMinutes(leg.takeoffTime, leg.landingTime);
      const hobbsStart = Number(leg.hobbsStart);
      const hobbsEnd = Number(leg.hobbsEnd);
      const hobbsDelta = Number.isFinite(hobbsStart) && Number.isFinite(hobbsEnd) && hobbsEnd >= hobbsStart
        ? Number((hobbsEnd - hobbsStart).toFixed(2))
        : null;

      return {
        minutes,
        decimalHours: Number.isFinite(minutes) ? Number((minutes / 60).toFixed(2)) : null,
        hobbsDelta,
      };
    })
  ), [legsWithCarryForward, calculateLegMinutes]);

  const totalFlightMinutes = legSummaries.reduce((sum, leg) => (
    Number.isFinite(leg.minutes) ? sum + leg.minutes : sum
  ), 0);

  const totalFlightHours = Number((totalFlightMinutes / 60).toFixed(2));
  const hasCalculatedFlightTime = totalFlightMinutes > 0;

  const totalMovement = legSummaries.reduce((sum, leg) => (
    Number.isFinite(leg.hobbsDelta) ? sum + leg.hobbsDelta : sum
  ), 0);

  const isDefectReported = aircraftStatus === 'defect_reported';
  const hasDefectDetails = !!(defect.trim() && defect.trim().toLowerCase() !== 'nil');
  const isDefectSaveBlocked = isDefectReported && (!defectSeverity || !hasDefectDetails);
  const saveButtonLabel = isDefectSaveBlocked ? 'COMPLETE DEFECT DETAILS TO SAVE' : 'SAVE TECH LOG ENTRY';

  useEffect(() => {
    const nextDefect = resolveDefect();
    const nextAircraftStatus = String(prefill.aircraftStatus || '').trim()
      || (nextDefect && nextDefect.toLowerCase() !== 'nil' ? 'defect_reported' : 'serviceable');

    setRecordingMethod(firstValue(prefill.recordingMethod, options.recordingMethod, 'hobbs'));
    setFlightNumber(prefill.flightNumber || '');
    setDepartureAirfield(normalizeAirfieldCode(prefill.departureAirfield));
    setArrivalAirfield(normalizeAirfieldCode(prefill.arrivalAirfield));
    setDepartureTime('');
    setArrivalTime('');
    const nextRouteType = String(
      firstValue(
        prefill.bookingFlightType,
        prefill.flightType,
        inferRouteTypeFromAirfields(
          prefill.departureAirfield,
          prefill.arrivalAirfield
        )
      )
    )
      .trim()
      .toLowerCase();

    setRouteType(
      ['local', 'circuits', 'land-away'].includes(
        nextRouteType
      )
        ? nextRouteType
        : ''
    );
    setLegs(buildInitialLegs());
    setMethodStart(firstValue(prefill.hobbsStart, prefill.methodStart, prefill.startReading));
    setMethodEnd(firstValue(prefill.hobbsEnd, prefill.methodEnd, prefill.endReading));
    setFlightTime(prefill.flightTime || '');
    setTaxiTime(resolveTaxiTime());
    setAircraftStatus(nextAircraftStatus);
    setDefect(nextDefect);
    setDefectSeverity(resolveDefectSeverity());
    setSignature(toInputString(firstValue(prefill.signature, prefill.pilotName)));
    setRectification(prefill.rectification || '');
    setEngineerSignature(prefill.engineerSignature || '');
    setNextCheck(prefill.nextCheck || '');
    setFuelUpliftLitres(toInputString(firstValue(prefill.fuelUpliftLitres, prefill.fuel_uplift_litres)));
    setOilUpliftLitres(toInputString(firstValue(prefill.oilUpliftLitres, prefill.oil_uplift_litres)));
    setRemarks(toInputString(firstValue(prefill.remarks, prefill.notes)));
  }, [prefill.flightNumber, prefill.bookingId, prefill.departureAirfield, prefill.arrivalAirfield, prefill.departureTime, prefill.arrivalTime, prefill.legs, prefill.bookingBaseAirfield, prefill.bookingDestinationAirfield, prefill.bookingReturnAirfield, prefill.bookingStartTime, prefill.bookingEndTime, prefill.bookingFlightType, prefill.flightType, prefill.methodStart, prefill.methodEnd, prefill.hobbsStart, prefill.hobbsEnd, prefill.startReading, prefill.endReading, prefill.flightTime, prefill.taxiTime, prefill.defect, prefill.snag, prefill.defectSeverity, prefill.defect_severity, prefill.aircraftStatus, prefill.signature, prefill.recordingMethod, prefill.rectification, prefill.engineerSignature, prefill.nextCheck, prefill.fuelUpliftLitres, prefill.fuel_uplift_litres, prefill.oilUpliftLitres, prefill.oil_uplift_litres, prefill.remarks, prefill.notes, prefill.pilotName, options.recordingMethod, options.defaultTaxiTimeEnabled, options.defaultTaxiTimeMinutes, options.defaultTaxiTimeHours, buildInitialLegs, normalizeAirfieldCode, resolveDefect, resolveDefectSeverity, resolveTaxiTime]);

  function updateLeg(index, field, value) {
    setValidationError('');
    setLegFieldErrors((prev) => {
      const key = `${index}:${field}`;
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });

    setLegs((prev) => prev.map((leg, i) => {
      if (i !== index) return leg;

      if (field === 'from' || field === 'to') {
        return { ...leg, [field]: normalizeAirfieldCode(value) };
      }

      return { ...leg, [field]: value };
    }));
  }

  function handleRouteTypeChange(value) {
    setValidationError('');
    setLegFieldErrors({});
    setRouteType(value);

    const baseAirfield = normalizeAirfieldCode(
      prefill.bookingBaseAirfield
    );

    setLegs((prev) => {
      const current =
        prev.length > 0
          ? prev.map(normalizeLeg)
          : [normalizeLeg()];

      const firstLeg =
        current[0] || normalizeLeg();

      if (
        value === 'local' ||
        value === 'circuits'
      ) {
        return [
          {
            ...firstLeg,
            from: baseAirfield,
            to: baseAirfield,
          },
          ...current.slice(1),
        ];
      }

      if (value === 'land-away') {
        return [
          {
            ...firstLeg,
            from:
              firstLeg.from ||
              baseAirfield,
            to: '',
          },
          ...current.slice(1),
        ];
      }

      return current;
    });
  }
  function addLeg() {
    setValidationError('');
    setLegFieldErrors({});
    setLegs((prev) => {
      const currentLegs = prev.length > 0 ? prev.map(normalizeLeg) : [normalizeLeg()];
      const previousLeg = currentLegs[currentLegs.length - 1] || normalizeLeg();
      const newLeg = normalizeLeg({
        from: previousLeg.to || '',
        to: '',
        hobbsStart: previousLeg.hobbsEnd || '',
        hobbsEnd: '',
      });

      return [...currentLegs, newLeg];
    });
  }

  function removeLeg(index) {
    setValidationError('');
    setLegFieldErrors({});
    setLegs((prev) => {
      if (prev.length <= 1) return [normalizeLeg()];
      return prev.filter((_, i) => i !== index);
    });
  }

  function hasLegFieldError(index, field) {
    return !!legFieldErrors[`${index}:${field}`];
  }

  function getInputStyle(hasError = false, disabled = false) {
    return {
      padding: '0.45rem',
      borderRadius: 6,
      border: hasError ? '1px solid #ff7f7f' : '1px solid #1ecfff',
      background: disabled ? '#25324c' : '#181a2a',
      color: disabled ? '#b6eaff' : 'white',
      width: '100%',
      boxSizing: 'border-box',
      opacity: disabled ? 0.9 : 1,
    };
  }

  function handleAircraftStatusChange(value) {
    setValidationError('');
    setAircraftStatus(value);

    if (value === 'serviceable') {
      setDefect('Nil');
      setDefectSeverity('');
      return;
    }

    if (defect.trim().toLowerCase() === 'nil') {
      setDefect('');
    }
  }

  function handleDefectChange(value) {
    setValidationError('');
    setDefect(value);

    if (value.trim() && value.trim().toLowerCase() !== 'nil') {
      setAircraftStatus('defect_reported');
    }
  }

  function handleDefectSeverityChange(value) {
    setValidationError('');
    setDefectSeverity(value);
    setAircraftStatus('defect_reported');

    if (defect.trim().toLowerCase() === 'nil') {
      setDefect('');
    }
  }

  function handleSave() {
    setValidationError('');
    setLegFieldErrors({});

    if (routeChoiceNeeded && !routeType) {
      setValidationError(
        'Select Local, Circuits or Land away before saving this older booking.'
      );
      return;
    }
    const normalizedLegs = (legsWithCarryForward || []).map(normalizeLeg);
    const populatedLegs = normalizedLegs.filter(isLegPopulated);

    if (populatedLegs.length === 0) {
      setValidationError('Add at least one leg before saving.');
      return;
    }

    const nextFieldErrors = {};

    for (let i = 0; i < populatedLegs.length; i += 1) {
      const leg = populatedLegs[i];

      if (!leg.from) nextFieldErrors[`${i}:from`] = true;
      if (!leg.to) nextFieldErrors[`${i}:to`] = true;
      if (!leg.takeoffTime) nextFieldErrors[`${i}:takeoffTime`] = true;
      if (!leg.landingTime) nextFieldErrors[`${i}:landingTime`] = true;

      const legMinutes = calculateLegMinutes(leg.takeoffTime, leg.landingTime);
      if (leg.takeoffTime && leg.landingTime && (!Number.isFinite(legMinutes) || legMinutes <= 0)) {
        nextFieldErrors[`${i}:takeoffTime`] = true;
        nextFieldErrors[`${i}:landingTime`] = true;
      }

      if (mergedOptions.hobbsEnabled && (leg.hobbsStart || leg.hobbsEnd)) {
        const startNum = Number(leg.hobbsStart);
        const endNum = Number(leg.hobbsEnd);

        if (!Number.isFinite(startNum)) nextFieldErrors[`${i}:hobbsStart`] = true;
        if (!Number.isFinite(endNum)) nextFieldErrors[`${i}:hobbsEnd`] = true;

        if (Number.isFinite(startNum) && Number.isFinite(endNum) && endNum < startNum) {
          nextFieldErrors[`${i}:hobbsStart`] = true;
          nextFieldErrors[`${i}:hobbsEnd`] = true;
        }
      }
    }

    const hasDefectReported = aircraftStatus === 'defect_reported';

    if (hasDefectReported && !defectSeverity && (!defect.trim() || defect.trim().toLowerCase() === 'nil')) {
      setValidationError(
        'DEFECT REPORTED is selected. Please choose a severity and enter defect details before saving.'
      );
      return;
    }

    if (hasDefectReported && !defectSeverity) {
      setValidationError(
        'DEFECT REPORTED is selected. Please choose a defect severity before saving.'
      );
      return;
    }

    if (hasDefectReported && (!defect.trim() || defect.trim().toLowerCase() === 'nil')) {
      setValidationError(
        'DEFECT REPORTED is selected. Please enter defect details before saving.'
      );
      return;
    }

    if (Object.keys(nextFieldErrors).length > 0) {
      setLegFieldErrors(nextFieldErrors);
      setValidationError(`Each leg needs From, To, Take-off time and Landing time. ${methodLabelTitle} values are optional, but must be valid if entered.`);
      return;
    }

    const numericTaxiHours = Number(taxiTime);

    const allowedTaxiHours =
      Number.isFinite(numericTaxiHours) &&
      numericTaxiHours > 0
        ? numericTaxiHours
        : 0;

    const maximumExpectedMovement =
      totalFlightHours +
      allowedTaxiHours +
      0.5;

    if (
      mergedOptions.hobbsEnabled &&
      hasCalculatedFlightTime &&
      totalMovement > 0 &&
      totalMovement > maximumExpectedMovement
    ) {
      setValidationError(
        `${methodLabelTitle} movement (${totalMovement.toFixed(
          1
        )} hours) is too high for ${formatMinutesAsHoursMinutes(
          totalFlightMinutes
        )} flight time. Check the ${methodLabelTitle} Out and In values before saving.`
      );
      return;
    }
    const firstLeg = populatedLegs[0];
    const lastLeg = populatedLegs[populatedLegs.length - 1];

    const flightTimeToSave = hasCalculatedFlightTime ? String(totalFlightHours) : flightTime;
    const takeoffTimeToSave = firstLeg.takeoffTime || departureTime || '';
    const landingTimeToSave = lastLeg.landingTime || arrivalTime || '';
    const methodStartToSave = firstValue(firstLeg.hobbsStart, methodStart, prefill.hobbsStart, prefill.methodStart, prefill.startReading);
    const methodEndToSave = firstValue(lastLeg.hobbsEnd, methodEnd, prefill.hobbsEnd, prefill.methodEnd, prefill.endReading);
    const departureAirfieldToSave = firstLeg.from || departureAirfield;
    const arrivalAirfieldToSave = lastLeg.to || arrivalAirfield;
    const nowIso = new Date().toISOString();

    if (onSave) {
      onSave({
        flightNumber,
        flightType:
          routeType ||
          initialRouteType ||
          '',        departureAirfield: departureAirfieldToSave,
        arrivalAirfield: arrivalAirfieldToSave,
        legs: populatedLegs.map((leg, index) => ({
          ...leg,
          flightMinutes: legSummaries[index]?.minutes ?? null,
          flightTime: legSummaries[index]?.decimalHours ?? null,
          hobbsDelta: legSummaries[index]?.hobbsDelta ?? null,
        })),
        departureTime: takeoffTimeToSave,
        arrivalTime: landingTimeToSave,
        recordingMethod,
        methodStart: methodStartToSave,
        methodEnd: methodEndToSave,
        hobbsStart: methodStartToSave,
        hobbsEnd: methodEndToSave,
        hobbsTotal: Number.isFinite(totalMovement) ? String(Number(totalMovement.toFixed(2))) : '',
        flightTime: flightTimeToSave,
        flightMinutes: totalFlightMinutes,
        flightTimeDisplay: formatMinutesAsHoursMinutes(totalFlightMinutes),
        taxiTime,
        aircraftStatus,
        serviceable: aircraftStatus === 'serviceable',
        defect,
        snag: defect,
        defectSeverity: hasDefectReported ? defectSeverity : '',
        defectStatus: hasDefectReported ? 'open' : 'none',
        defectReportedAt: hasDefectReported ? (prefill.defectReportedAt || nowIso) : '',
        defectReportedBy: hasDefectReported ? (prefill.pilotName || '') : '',
        signature,
        nextCheck,
        rectification,
        engineerSignature,
        fuelUpliftLitres,
        oilUpliftLitres,
        checkCompleted: false,
        checkType: '',
        checkCompletedAt: '',
        nextCheckDueAt: '',
        checkCountdownHours: '',
        flightDate: prefill.flightDate || '',
        pilotName: prefill.pilotName || '',
        remarks,
        notes: remarks,
      });
    }
  }

  return (
    <div style={{ background: '#162238', border: '1px solid #2f466d', borderRadius: 10, padding: '0.75rem', color: '#b6eaff', marginBottom: '1rem' }}>
      {!!validationError && (
        <div style={{ marginBottom: '0.9rem', color: '#ff9f9f', background: '#3a1d1d', border: '1px solid #7a3030', borderRadius: 8, padding: '0.45rem 0.6rem' }}>
          {validationError}
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns:
            'repeat(auto-fit, minmax(360px, 1fr))',
          gap: '0.75rem',
          alignItems: 'start',
          marginBottom: '0.8rem',
        }}
      >      <div style={{ marginBottom: 0, padding: '0.65rem', background: '#1a2338', border: '1px solid #2f466d', borderRadius: 8 }}>
        <div style={{ color: '#d7ebff', fontWeight: 800, fontSize: '0.9rem', letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: '0.55rem' }}>
          FLIGHT LEGS
        </div>
        {routeChoiceNeeded ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns:
                'minmax(150px, 240px) minmax(200px, 1fr)',
              gap: '0.65rem',
              alignItems: 'end',
              marginBottom: '0.65rem',
              padding: '0.6rem',
              background: '#10233a',
              border:
                '1px solid rgba(85,199,255,0.28)',
              borderRadius: 8,
            }}
          >
            <label>
              <div
                style={{
                  color: '#9fdcff',
                  fontSize: '0.72rem',
                  fontWeight: 800,
                  marginBottom: 4,
                }}
              >
                Flight type
              </div>

              <select
                value={routeType}
                onChange={(event) =>
                  handleRouteTypeChange(
                    event.target.value
                  )
                }
                style={compactInputStyle}
              >
                <option value="">
                  Select flight type
                </option>

                <option value="local">
                  Local
                </option>

                <option value="circuits">
                  Circuits
                </option>

                <option value="land-away">
                  Land away
                </option>
              </select>
            </label>

            <div
              style={{
                color: '#8db7e6',
                fontSize: '0.75rem',
                lineHeight: 1.4,
                paddingBottom: '0.45rem',
              }}
            >
              Older booking route not recorded.
              Local or Circuits uses the base
              airfield automatically.
            </div>
          </div>
        ) : null}

        <div style={{ color: '#8db7e6', fontSize: '0.78rem', lineHeight: 1.4, marginBottom: '0.6rem' }}>
          Add one row for each sector flown. Wingit carries the previous {methodLabelTitle} In value forward as the next leg&apos;s {methodLabelTitle} Out.
        </div>

        <div style={{ display: 'grid', gap: '0.75rem' }}>
          {legsWithCarryForward.map((leg, index) => {
            const summary = legSummaries[index] || {};
            const carryForwardStart = index > 0 && !!leg.hobbsStart;

            return (
              <div
                key={`leg-${index}`}
                style={{
                  padding: '0.6rem',
                  background: '#181a2a',
                  border: '1px solid #2f466d',
                  borderRadius: 10,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.8rem', alignItems: 'center', marginBottom: '0.6rem' }}>
                  <div style={{ color: '#d7ebff', fontWeight: 800, fontSize: '0.88rem' }}>
                    LEG {index + 1}
                  </div>

                  <button
                    type="button"
                    onClick={() => removeLeg(index)}
                    style={{ background: '#ff7c7c', color: '#290707', border: 'none', borderRadius: 6, padding: '0.24rem 0.48rem', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer' }}
                    title="Remove leg"
                  >
                    REMOVE
                  </button>
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: mergedOptions.hobbsEnabled
                      ? 'repeat(auto-fit, minmax(125px, 1fr))'
                      : 'repeat(auto-fit, minmax(140px, 1fr))',
                    gap: '0.55rem',
                    alignItems: 'end',
                  }}
                >
                  <label>
                    <div style={{ color: '#9fdcff', fontSize: '0.82rem', marginBottom: 3 }}>FROM</div>
                    <input
                      type="text"
                      value={leg.from}
                      placeholder="EGTR"
                      onChange={(e) => updateLeg(index, 'from', e.target.value)}
                      style={getInputStyle(hasLegFieldError(index, 'from'))}
                    />
                  </label>

                  <label>
                    <div style={{ color: '#9fdcff', fontSize: '0.82rem', marginBottom: 3 }}>TO</div>
                    <input
                      type="text"
                      value={leg.to}
                      placeholder="EGSU"
                      onChange={(e) => updateLeg(index, 'to', e.target.value)}
                      style={getInputStyle(hasLegFieldError(index, 'to'))}
                    />
                  </label>

                  <label>
                    <div style={{ color: '#9fdcff', fontSize: '0.82rem', marginBottom: 3 }}>TAKE-OFF</div>
                    <input
                      type="time"
                      value={leg.takeoffTime}
                      onChange={(e) => updateLeg(index, 'takeoffTime', e.target.value)}
                      style={getInputStyle(hasLegFieldError(index, 'takeoffTime'))}
                    />
                  </label>

                  <label>
                    <div style={{ color: '#9fdcff', fontSize: '0.82rem', marginBottom: 3 }}>LANDING</div>
                    <input
                      type="time"
                      value={leg.landingTime}
                      onChange={(e) => updateLeg(index, 'landingTime', e.target.value)}
                      style={getInputStyle(hasLegFieldError(index, 'landingTime'))}
                    />
                  </label>

                  {mergedOptions.hobbsEnabled && (
                    <label>
                      <div style={{ color: '#9fdcff', fontSize: '0.82rem', marginBottom: 3 }}>
                        {methodLabelTitle.toUpperCase()} OUT
                      </div>
                      <input
                        type="number"
                        min="0"
                        step="0.1"
                        value={leg.hobbsStart || ''}
                        placeholder={index === 0 ? 'Start' : 'Carried forward'}
                        disabled={carryForwardStart}
                        onChange={(e) => updateLeg(index, 'hobbsStart', e.target.value)}
                        style={getInputStyle(hasLegFieldError(index, 'hobbsStart'), carryForwardStart)}
                      />
                    </label>
                  )}

                  {mergedOptions.hobbsEnabled && (
                    <label>
                      <div style={{ color: '#9fdcff', fontSize: '0.82rem', marginBottom: 3 }}>
                        {methodLabelTitle.toUpperCase()} IN
                      </div>
                      <input
                        type="number"
                        min="0"
                        step="0.1"
                        value={leg.hobbsEnd || ''}
                        placeholder="End"
                        onChange={(e) => updateLeg(index, 'hobbsEnd', e.target.value)}
                        style={getInputStyle(hasLegFieldError(index, 'hobbsEnd'))}
                      />
                    </label>
                  )}
                </div>

                <div style={{ marginTop: '0.6rem', color: '#d7ebff', fontSize: '0.92rem' }}>
                  <strong>LEG FLIGHT TIME:</strong>{' '}
                  {Number.isFinite(summary.minutes)
                    ? `${formatMinutesAsHoursMinutes(summary.minutes)} (${summary.decimalHours.toFixed(2)} hours)`
                    : 'Enter take-off and landing time'}
                  {Number.isFinite(summary.hobbsDelta) ? (
                    <span style={{ marginLeft: 10, color: '#8db7e6' }}>
                      {methodLabelTitle}: {summary.hobbsDelta.toFixed(1)}
                    </span>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>

        <button
          type="button"
          onClick={addLeg}
          style={{ marginTop: '0.75rem', background: '#b6eaff', color: '#181a2a', border: 'none', borderRadius: 8, padding: '0.45rem 0.8rem', fontWeight: 700, cursor: 'pointer' }}
        >
          ADD LEG
        </button>
      </div>

        <div
          style={{
            display: 'grid',
            gap: '0.75rem',
            alignContent: 'start',
          }}
        >      <div style={{ marginBottom: 0, padding: '0.7rem', fontSize: '0.82rem', lineHeight: 1.45, background: '#10233a', border: '1px solid #35507f', borderRadius: 10 }}>
        <div style={{ color: '#d7ebff', fontWeight: 800, fontSize: '0.9rem', letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 5 }}>
          FLIGHT TIME SUMMARY
        </div>

        <div>
          TOTAL FLIGHT TIME:{' '}
          <strong style={{ color: '#1ecfff' }}>
            {hasCalculatedFlightTime ? `${formatMinutesAsHoursMinutes(totalFlightMinutes)} (${totalFlightHours.toFixed(2)} hours)` : 'Enter leg times'}
          </strong>
        </div>

        {mergedOptions.hobbsEnabled ? (
          <div style={{ marginTop: 4 }}>
            TOTAL {methodLabelTitle.toUpperCase()} MOVEMENT:{' '}
            <strong>
              {totalMovement > 0 ? totalMovement.toFixed(1) : 'Optional'}
            </strong>
          </div>
        ) : null}
      </div>

      <div style={{ marginBottom: 0, padding: '0.7rem', fontSize: '0.84rem', lineHeight: 1.4, background: '#1a2338', border: '1px solid #35507f', borderRadius: 10 }}>
        <div style={{ color: '#d7ebff', fontWeight: 800, fontSize: '0.9rem', letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 8 }}>
          AIRCRAFT STATUS
        </div>

        <label style={{ display: 'block', marginBottom: 8 }}>
          <input
            type="radio"
            name="aircraftStatus"
            value="serviceable"
            checked={aircraftStatus === 'serviceable'}
            onChange={() => handleAircraftStatusChange('serviceable')}
          />
          {' '}SERVICEABLE
        </label>

        <label style={{ display: 'block', marginBottom: 8 }}>
          <input
            type="radio"
            name="aircraftStatus"
            value="defect_reported"
            checked={aircraftStatus === 'defect_reported'}
            onChange={() => handleAircraftStatusChange('defect_reported')}
          />
          {' '}DEFECT REPORTED
        </label>

        {aircraftStatus === 'defect_reported' && (
          <div style={{ marginTop: '0.9rem', padding: '0.75rem', background: '#10233a', border: '1px solid #ffcc66', borderRadius: 10 }}>
            <div style={{ color: '#ffcc66', fontWeight: 900, marginBottom: 8 }}>
              ⚠ DEFECT REPORTED
            </div>

            <div style={{ color: '#ffd98c', fontSize: '0.92rem', lineHeight: 1.45, marginBottom: '0.8rem' }}>
              IF A DEFECT IS REPORTED, DEFECT SEVERITY AND DEFECT DETAILS MUST BE COMPLETED BEFORE THE TECH LOG CAN BE SAVED.
            </div>

            <div style={{ color: '#ffcc66', fontWeight: 800, marginBottom: 8 }}>
              DEFECT SEVERITY *
            </div>

            <label style={{ display: 'block', marginBottom: 8 }}>
              <input
                type="radio"
                name="defectSeverity"
                value="minor"
                checked={defectSeverity === 'minor'}
                onChange={() => handleDefectSeverityChange('minor')}
              />
              {' '}MINOR
            </label>

            <label style={{ display: 'block', marginBottom: 8 }}>
              <input
                type="radio"
                name="defectSeverity"
                value="significant"
                checked={defectSeverity === 'significant'}
                onChange={() => handleDefectSeverityChange('significant')}
              />
              {' '}SIGNIFICANT
            </label>

            <label style={{ display: 'block', marginBottom: 10 }}>
              <input
                type="radio"
                name="defectSeverity"
                value="safety_critical"
                checked={defectSeverity === 'safety_critical'}
                onChange={() => handleDefectSeverityChange('safety_critical')}
              />
              {' '}SAFETY CRITICAL
            </label>

            <label style={{ display: 'block' }}>
              <div style={{ color: '#d7ebff', fontWeight: 800, fontSize: '0.9rem', letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 5 }}>
                DEFECT DETAILS *
              </div>
              <textarea
                value={defect}
                onChange={e => handleDefectChange(e.target.value)}
                rows={4}
                placeholder="Describe the defect clearly for the owner or engineer."
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: '0.55rem',
                  borderRadius: 8,
                  border: '1px solid #1ecfff',
                  background: '#181a2a',
                  color: 'white',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                }}
              />
            </label>
          </div>
        )}
      </div>

        </div>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns:
            'repeat(auto-fit, minmax(340px, 1fr))',
          gap: '0.75rem',
          marginBottom: '0.8rem',
        }}
      >
        <div
          style={{
            background: '#1a2338',
            border: '1px solid #35507f',
            borderRadius: 10,
            padding: '0.75rem',
          }}
        >
          <div
            style={{
              color: '#d7ebff',
              fontSize: '0.88rem',
              fontWeight: 800,
              marginBottom: '0.65rem',
            }}
          >
            Flight details
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns:
                'repeat(auto-fit, minmax(150px, 1fr))',
              gap: '0.65rem',
            }}
          >
            {mergedOptions.showOptionalFields && (
              <label>
                <div style={compactLabelStyle}>
                  Flight number
                </div>

                <input
                  type="text"
                  value={flightNumber}
                  onChange={(e) =>
                    setFlightNumber(e.target.value)
                  }
                  style={compactInputStyle}
                />
              </label>
            )}

            {mergedOptions.showOptionalFields && (
              <label>
                <div style={compactLabelStyle}>
                  Departure airfield
                </div>

                <input
                  type="text"
                  value={departureAirfield}
                  onChange={(e) =>
                    setDepartureAirfield(
                      e.target.value.toUpperCase()
                    )
                  }
                  style={compactInputStyle}
                />
              </label>
            )}

            {mergedOptions.showOptionalFields && (
              <label>
                <div style={compactLabelStyle}>
                  Arrival airfield
                </div>

                <input
                  type="text"
                  value={arrivalAirfield}
                  onChange={(e) =>
                    setArrivalAirfield(
                      e.target.value.toUpperCase()
                    )
                  }
                  style={compactInputStyle}
                />
              </label>
            )}

            {mergedOptions.flightTimeEnabled && (
              <label>
                <div style={compactLabelStyle}>
                  Billing time
                </div>

                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={
                    hasCalculatedFlightTime
                      ? String(totalFlightHours)
                      : flightTime
                  }
                  onChange={(e) =>
                    setFlightTime(e.target.value)
                  }
                  disabled={hasCalculatedFlightTime}
                  style={{
                    ...compactInputStyle,
                    background: hasCalculatedFlightTime
                      ? '#25324c'
                      : '#181a2a',
                  }}
                />

                <div style={compactHintStyle}>
                  {hasCalculatedFlightTime
                    ? 'Calculated from leg times'
                    : 'Decimal hours'}
                </div>
              </label>
            )}

            {mergedOptions.taxiTimeEnabled && (
              <label>
                <div style={compactLabelStyle}>
                  Taxi time
                </div>

                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={taxiTime}
                  onChange={(e) =>
                    setTaxiTime(e.target.value)
                  }
                  style={compactInputStyle}
                />

                <div style={compactHintStyle}>
                  Decimal hours
                </div>
              </label>
            )}
          </div>
        </div>

        <div
          style={{
            background: '#1a2338',
            border: '1px solid #35507f',
            borderRadius: 10,
            padding: '0.75rem',
          }}
        >
          <div
            style={{
              color: '#d7ebff',
              fontSize: '0.88rem',
              fontWeight: 800,
              marginBottom: '0.65rem',
            }}
          >
            Aircraft details
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns:
                'repeat(auto-fit, minmax(150px, 1fr))',
              gap: '0.65rem',
            }}
          >
            <label>
              <div style={compactLabelStyle}>
                Fuel uplift (L)
              </div>

              <input
                type="number"
                min="0"
                step="0.1"
                value={fuelUpliftLitres}
                onChange={(e) =>
                  setFuelUpliftLitres(e.target.value)
                }
                style={compactInputStyle}
              />
            </label>

            <label>
              <div style={compactLabelStyle}>
                Oil uplift (L)
              </div>

              <input
                type="number"
                min="0"
                step="0.1"
                value={oilUpliftLitres}
                onChange={(e) =>
                  setOilUpliftLitres(e.target.value)
                }
                style={compactInputStyle}
              />
            </label>

            {mergedOptions.showOptionalFields && (
              <label>
                <div style={compactLabelStyle}>
                  Next check
                </div>

                <input
                  type="text"
                  value={nextCheck}
                  onChange={(e) =>
                    setNextCheck(e.target.value)
                  }
                  style={compactInputStyle}
                />
              </label>
            )}

            {mergedOptions.showOptionalFields && (
              <label>
                <div style={compactLabelStyle}>
                  Signature
                </div>

                <input
                  type="text"
                  value={signature}
                  onChange={(e) =>
                    setSignature(e.target.value)
                  }
                  style={compactInputStyle}
                />
              </label>
            )}

            {mergedOptions.showOptionalFields && (
              <label>
                <div style={compactLabelStyle}>
                  Engineer signature
                </div>

                <input
                  type="text"
                  value={engineerSignature}
                  onChange={(e) =>
                    setEngineerSignature(e.target.value)
                  }
                  style={compactInputStyle}
                />
              </label>
            )}

            {mergedOptions.showOptionalFields && (
              <label>
                <div style={compactLabelStyle}>
                  Rectification
                </div>

                <input
                  type="text"
                  value={rectification}
                  onChange={(e) =>
                    setRectification(e.target.value)
                  }
                  style={compactInputStyle}
                />
              </label>
            )}
          </div>

          {mergedOptions.notesEnabled && (
            <label
              style={{
                display: 'block',
                marginTop: '0.65rem',
              }}
            >
              <div style={compactLabelStyle}>
                Remarks
              </div>

              <input
                type="text"
                value={remarks}
                onChange={(e) =>
                  setRemarks(e.target.value)
                }
                style={compactInputStyle}
              />
            </label>
          )}
        </div>
      </div>
      <button
        onClick={handleSave}
        disabled={isDefectSaveBlocked}
        title={isDefectSaveBlocked ? 'Choose defect severity and enter defect details before saving.' : 'Save Tech Log Entry'}
        style={{
          background: isDefectSaveBlocked ? '#536176' : '#1ecfff',
          color: isDefectSaveBlocked ? '#d7ebff' : '#181a2a',
          border: 'none',
          borderRadius: 8,
          padding: '0.5rem 1.2rem',
          fontWeight: 600,
          cursor: isDefectSaveBlocked ? 'not-allowed' : 'pointer',
          opacity: isDefectSaveBlocked ? 0.78 : 1,
        }}
      >
        {saveButtonLabel}
      </button>
    </div>
  );
}